import { cn } from '@/lib/utils';
import { GarminAttribution } from './GarminAttribution';
import { AppleHealthAttribution } from './AppleHealthAttribution';

type ActivitySource = 'strava' | 'garmin' | 'apple_health' | 'manual';

interface ActivitySourceStickerProps {
  source: ActivitySource;
  className?: string;
  /** Device model name from Garmin (e.g. "Forerunner 265"). */
  deviceModel?: string | null;
  /** Source app name from Apple Health (e.g. "Apple Watch"). */
  sourceApp?: string | null;
  /** When true (default for garmin/apple_health), show the required attribution text below the badge. */
  showAttribution?: boolean;
}

/** Sticker badge showing Strava, Garmin, or Apple Health logo on activity details (popup/expansion). */
export function ActivitySourceSticker({ source, className, deviceModel, sourceApp, showAttribution = true }: ActivitySourceStickerProps) {
  if (source === 'manual') return null;

  if (source === 'strava') {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium',
          'bg-[#FC4C02]/15 text-[#FC4C02] border border-[#FC4C02]/30',
          className
        )}
        title="From Strava"
      >
        <StravaLogoSvg className="h-3.5 w-3.5" />
        Strava
      </span>
    );
  }

  if (source === 'garmin') {
    const tooltip = deviceModel ? `From Garmin ${deviceModel}` : 'From Garmin';
    return (
      <span className={cn('inline-flex flex-col gap-1', className)}>
        <span
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium w-fit',
            'bg-[#007CC3]/15 text-[#007CC3] border border-[#007CC3]/30'
          )}
          title={tooltip}
        >
          {/* Official Garmin Tag logo (white variant for dark theme) per Brand Guidelines */}
          <img
            src="/garmin-tag-white.png"
            alt="Garmin"
            className="h-3.5 object-contain"
          />
          {deviceModel || null}
        </span>
        {showAttribution && <GarminAttribution deviceModel={deviceModel} />}
      </span>
    );
  }

  if (source === 'apple_health') {
    const label = sourceApp || 'Apple Health';
    const tooltip = `From ${label}`;
    return (
      <span className={cn('inline-flex flex-col gap-1', className)}>
        <span
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium w-fit',
            'bg-[#FF2D55]/15 text-[#FF2D55] border border-[#FF2D55]/30'
          )}
          title={tooltip}
        >
          <AppleHealthSvg className="h-3.5 w-3.5" />
          {label}
        </span>
        {showAttribution && <AppleHealthAttribution sourceApp={sourceApp} />}
      </span>
    );
  }

  return null;
}

function StravaLogoSvg({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden
    >
      <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.599h4.172L10.463 0l-7 13.828h4.169" />
    </svg>
  );
}

function AppleHealthSvg({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden
    >
      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
    </svg>
  );
}
